import { userByIdOptions } from "@/api/queryApi";
import { LanguageSelector } from "@/features/header/toolbar/LanguageSelector";
import Pfp from "@/features/header/toolbar/Pfp";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSignOutAlt } from "@fortawesome/sharp-regular-svg-icons";
import { useQuery } from "@tanstack/react-query";
import { useJwt, useLogout } from "frontend-auth";
import {
    cn,
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "frontend-components";
import { useTranslation } from "react-i18next";

export default function UserMenu({ variant = "full" }: { variant?: "full" | "compact" }) {
    const userId = useJwt((store) => store.userId);
    const { data: user } = useQuery(userByIdOptions({ id: userId }));
    const logout = useLogout();
    const { t } = useTranslation();

    const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(" ");

    return (
        <DropdownMenu>
            <DropdownMenuTrigger
                aria-label={t("dropdown.menu")}
                className={cn(
                    "focus-visible:ring-outline flex cursor-pointer items-center outline-none transition-colors focus-visible:ring-2 focus-visible:ring-offset-2",
                    variant === "full"
                        ? "hover:bg-muted w-full gap-2.5 px-4 py-3 text-left"
                        : "size-9 shrink-0 justify-center rounded-full",
                )}
            >
                <span className="bg-card ring-border inline-flex h-7 w-7 shrink-0 items-center justify-center overflow-hidden rounded-full ring-1">
                    {userId && <Pfp userId={userId} />}
                </span>
                {variant === "full" && (
                    <span className="min-w-0 flex-1">
                        <span className="block overflow-hidden text-ellipsis whitespace-nowrap text-xs leading-tight font-medium">
                            {fullName}
                        </span>
                        {user?.email && (
                            <span className="text-muted-foreground block overflow-hidden text-ellipsis whitespace-nowrap text-[11px]">
                                {user.email}
                            </span>
                        )}
                    </span>
                )}
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-54">
                <DropdownMenuGroup>
                    <DropdownMenuLabel>
                        {user?.firstName} {user?.lastName}
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <LanguageSelector />
                    <DropdownMenuItem onClick={logout}>
                        <FontAwesomeIcon icon={faSignOutAlt} />
                        {t("common.logout")}
                    </DropdownMenuItem>
                </DropdownMenuGroup>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
